<template>
    <RegularModal ref="modal">
        <template v-slot:title>{{ translate("page.category_manager.delete_categories") }}</template>

        <template v-slot:body>
            <div v-if="!initDone" class="columns is-centered">
                <LoadingSpinner2 class="has-added-margin" />
            </div>
            <div v-else class="columns">
                <div class="column">
                    <div v-if="isDeletePossible" class="is-size-5 has-text-centered has-text-danger">
                        {{ translate("page.category_manager.sure_to_delete_all") }}
                    </div>
                    <template v-else>
                        <p class="is-size-5 has-text-centered">
                            {{ translate("page.category_manager.cannot_delete_categories_due_registrations") }}
                        </p>
                        <p class="has-text-centered mt-3">
                            <span class="tag is-rounded is-danger is-medium">{{ overallRegistrations }}</span>
                        </p>
                    </template>
                    <ul class="mt-3">
                        <li
                            :class="{ 'has-text-danger': category.totalRegistrations > 0 }"
                            v-for="category in categories"
                            :key="category.id"
                        >
                            {{ category.name }} ({{ category.totalRegistrations }})
                        </li>
                    </ul>
                </div>
            </div>
        </template>

        <template v-slot:footer>
            <div class="buttons">
                <button class="button" @click="close">{{ translate("common.cancel") }}</button>
                <LoadingButton
                    :is-loading="deletingInProgress"
                    class="button is-danger"
                    :disabled="!isDeletePossible"
                    @click="deleteCategories"
                >
                    <span class="icon">
                        <i class="fa fa-trash-alt" />
                    </span>
                    <span>{{ translate("common.delete_all") }}</span>
                </LoadingButton>
            </div>
        </template>
    </RegularModal>
</template>

<script setup lang="ts">
    import { ref, computed } from "vue";
    import RegularModal from "../Common/RegularModal.vue";
    import categoriesApi, { ParameterLeafStatus } from "../Api/Categories/categories";
    import { handleError } from "../Common/errorHandler";
    import { translate } from "../Common/translations";
    import LoadingSpinner2 from "../Common/LoadingSpinner2.vue";
    import LoadingButton from "../Common/LoadingButton.vue";
    import { successToast } from "../Common/toasts";
    import { useCategoriesManagerStore } from "./categoriesManagerStore";
    import { useModal } from "../Common/useModal";

    const store = useCategoriesManagerStore();
    const { open: openModal, close, modal } = useModal();

    const nodeId = ref<number>(0);
    const categories = ref<ParameterLeafStatus[]>([]);
    const initDone = ref(false);
    const deletingInProgress = ref(false);

    const overallRegistrations = computed(() => {
        let counter = 0;
        categories.value.forEach(category => {
            counter += category.totalRegistrations;
        });
        return counter;
    });

    const isDeletePossible = computed(() => {
        return overallRegistrations.value === 0;
    });

    function open(nodeIdParam: number) {
        initDone.value = false;
        categories.value = [];
        nodeId.value = nodeIdParam;
        openModal();

        return categoriesApi
            .getTreeStatusForParameter(store.eventId, nodeIdParam)
            .then(categoriesResponse => (categories.value = categoriesResponse))
            .catch(handleError)
            .finally(() => {
                initDone.value = true;
            });
    }

    function deleteCategories() {
        deletingInProgress.value = true;
        return categoriesApi
            .deleteParameterTree(store.eventId, nodeId.value)
            .then(() => {
                return store.refreshCategories();
            })
            .then(() => {
                close();
                successToast(translate("page.category_manager.categories_deleted"));
            })
            .finally(() => {
                deletingInProgress.value = false;
            });
    }

    defineExpose({
        open,
    });
</script>

<style scoped></style>
